import { useState, useCallback } from "react"

interface HistoryEntry {
  selectedNodes: string[]
  visibleSteps: number
}

export default function useJourneyNavigation() {
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [visibleSteps, setVisibleSteps] = useState(1)
  const [history, setHistory] = useState<HistoryEntry[]>([
    { selectedNodes: [], visibleSteps: 1 },
  ])
  const [historyIndex, setHistoryIndex] = useState(0)

  const selectNode = useCallback(
    (nodeName: string, stepIndex: number) => {
      const newSelectedNodes = [...selectedNodes.slice(0, stepIndex), nodeName]
      const newVisibleSteps = stepIndex + 2 // show the next step

      setSelectedNodes(newSelectedNodes)
      setVisibleSteps(Math.min(newVisibleSteps, 3))

      const newEntry: HistoryEntry = {
        selectedNodes: newSelectedNodes,
        visibleSteps: Math.min(newVisibleSteps, 3),
      }
      const newHistory = [...history.slice(0, historyIndex + 1), newEntry]
      setHistory(newHistory)
      setHistoryIndex(newHistory.length - 1)
    },
    [selectedNodes, history, historyIndex],
  )

  const goBack = useCallback(() => {
    if (historyIndex <= 0) return
    const newIndex = historyIndex - 1
    const entry = history[newIndex]
    setHistoryIndex(newIndex)
    setSelectedNodes(entry.selectedNodes)
    setVisibleSteps(entry.visibleSteps)
  }, [historyIndex, history])

  const goForward = useCallback(() => {
    if (historyIndex >= history.length - 1) return
    const newIndex = historyIndex + 1
    const entry = history[newIndex]
    setHistoryIndex(newIndex)
    setSelectedNodes(entry.selectedNodes)
    setVisibleSteps(entry.visibleSteps)
  }, [historyIndex, history])

  const navigateToStep = useCallback(
    (stepNumber: number) => {
      const newSelectedNodes = selectedNodes.slice(0, stepNumber - 1)
      const newVisibleSteps = stepNumber

      setSelectedNodes(newSelectedNodes)
      setVisibleSteps(newVisibleSteps)

      const newEntry: HistoryEntry = {
        selectedNodes: newSelectedNodes,
        visibleSteps: newVisibleSteps,
      }
      const newHistory = [...history.slice(0, historyIndex + 1), newEntry]
      setHistory(newHistory)
      setHistoryIndex(newHistory.length - 1)
    },
    [selectedNodes, history, historyIndex],
  )

  const startOver = useCallback(() => {
    const initialEntry: HistoryEntry = {
      selectedNodes: [],
      visibleSteps: 1,
    }
    setSelectedNodes([])
    setVisibleSteps(1)
    setHistory([initialEntry])
    setHistoryIndex(0)
  }, [])

  return {
    selectedNodes,
    visibleSteps,
    canGoBack: historyIndex > 0,
    canGoForward: historyIndex < history.length - 1,
    selectNode,
    navigateToStep,
    goBack,
    goForward,
    startOver,
  }
}
